import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './index.css';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';
import useGoogleSignIn from './flow/useGoogleSignIn';
import {
  AppText,
  Button,
  Divider,
  InputField,
  Link,
} from '@zippeditoolsjs/ui-web';

export default function LoginForm(props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    tenantID,
    language,
    setIsLoading,
    isLoading,
    isLoadingRedirect,
    isLoadingSession,
    setLoginErrorKey,
  } = props;
  const [showPassword, setShowPassword] = useState(false);
  // Which button started the sign-in, so only that one spins.
  const [activeMethod, setActiveMethod] = useState(null);
  const googleButtonRef = useRef(null);

  // Folds in Google's own in-flight flag (see useGoogleSignIn) so neither
  // leg, nor the CSRF bootstrap, can start while the other already holds
  // auth.tenantId.
  const isBusy = isLoadingRedirect || isLoadingSession || isLoading;
  // Until a method is picked, an unrequested load (CSRF bootstrap) spins
  // both buttons instead of leaving two dead ones.
  const isSpinning = method =>
    isBusy && (activeMethod === null || activeMethod === method);
  const url_forgot_pass = '/recover?mode=confirmEmail&tenantId=' + tenantID;

  // GIS renders its own button into googleButtonRef and resolves the real
  // tenant before Firebase sees a credential — see useGoogleSignIn.
  // `|| {}` covers this file's own tests, where the hook is mocked bare.
  const { isBridge, startBridgeSignIn } =
    useGoogleSignIn({
      buttonRef: googleButtonRef,
      tenantID,
      language,
      isBusy,
      resolveTenant: props.resolveTenant,
      getSessionCookie: props.getSessionCookie,
      setResolverAuth: props.setResolverAuth,
      showMfaEnrollmentChooser: props.showMfaEnrollmentChooser,
      setLoginErrorKey,
      setIsLoading,
      setActiveMethod,
    }) || {};

  const handleClickShowPassword = () => setShowPassword(show => !show);

  const handleMouseDownPassword = event => {
    event.preventDefault();
  };

  return (
    <div className="flex flex-col">
      <AppText variant="titleXLarge" as="h3" className="mb-2">
        {t('heimdal_app.Login.welcome')}
      </AppText>
      <AppText variant="bodyMedium" as="p" className="mb-6 text-secondary">
        {t('heimdal_app.Login.welcome_subtitle')}
      </AppText>

      <InputField
        id="login-email"
        className="mb-3.5"
        label={t('heimdal_app.Login.email')}
        value={props.email}
        type="email"
        name="email"
        autoFocus
        onChange={props.handleChangeEmail}
      />

      <InputField
        id="login-password"
        className="mb-3.5"
        label={t('heimdal_app.Login.password')}
        type={showPassword ? 'text' : 'password'}
        name="password"
        value={props.password}
        onChange={props.handleChangePassword}
        endAdornment={
          <button
            type="button"
            aria-label="toggle password visibility"
            onClick={handleClickShowPassword}
            onMouseDown={handleMouseDownPassword}
            className="flex cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-1 hover:bg-accent-tint">
            <IconComponent
              iconName={showPassword ? 'eye-off-outline' : 'eye-outline'}
              className={'icon'}
              style={{ color: 'var(--zippedi-text-primary)' }}
            />
          </button>
        }
      />

      <div className="mb-[18px] mt-0.5 flex justify-end">
        <Link onClick={() => navigate(url_forgot_pass)}>
          {t('heimdal_app.Login.forgot_password')}
        </Link>
      </div>

      <Button
        fullWidth
        loading={isSpinning('email')}
        disabled={isBusy}
        onClick={event => {
          setActiveMethod('email');
          props.SignInWithEmail(event);
        }}>
        {t('heimdal_app.Login.sign_in')}
      </Button>

      <Divider className="my-4">
        {t('heimdal_app.Login.email_option_label')}
      </Divider>

      {isBridge ? (
        <Button
          fullWidth
          loading={isSpinning('google')}
          disabled={isBusy}
          onClick={startBridgeSignIn}
          startIcon={
            <IconComponent
              iconName="logo-google"
              className="icon"
              style={{ fontSize: '1.25rem' }}
            />
          }>
          {t('heimdal_app.Login.sign_in_with_zippedi')}
        </Button>
      ) : (
        <div ref={googleButtonRef} data-testid="google-signin-button" />
      )}
    </div>
  );
}
